import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SharedNav from '@/components/SharedNav';
import { API, useAuth } from '@/App';
import {
  ArrowLeft,
  Loader2,
  Inbox,
  ShieldCheck,
  Target,
  History,
  ArrowRight,
  Send,
} from 'lucide-react';
import ValidationStatus from '@/components/predict/ValidationStatus';
import DisclaimerBanner from '@/components/predict/DisclaimerBanner';

/**
 * /predict/validation
 * Sprint 0 — empty-state scaffolding. Sprint 5 fills it with real pilot
 * RMSE / MAE per module per crop per state from the predict_validation
 * MongoDB collection.
 */

const TARGETS = {
  'historical-yield': '100 % match against state agri-statistics yearbooks (it\'s history).',
  'water-calculator': 'RMSE < 0.5 mm/day vs lysimeter-published ETc (2 cultivars × 2 seasons).',
  'pest-tracker': '≤ 5-day lead/lag vs ICAR-NCIPM bulletin emergence dates.',
  'budget': '< 5 % deviation vs CACP cost-of-cultivation tables (5 crops × 3 states).',
};

export default function ValidationDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/predict/validation/summary`)
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Failed to load validation summary.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API stable; setters stable
  }, []);

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SharedNav />

      <main className="max-w-[1100px] mx-auto px-6 pt-10 pb-24" data-testid="validation-dashboard-page">
        <button
          onClick={() => navigate('/predict')}
          className="inline-flex items-center gap-1.5 mb-6 transition-colors"
          style={{ fontSize: 13, color: '#6F6A63', background: 'transparent', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#2D6A4F'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#6F6A63'; }}
          data-testid="validation-back"
        >
          <ArrowLeft size={14} /> Back to Predict
        </button>

        <header className="mb-8" data-testid="validation-header">
          <div className="inline-flex items-center gap-2 mb-3">
            <ShieldCheck size={16} style={{ color: '#2D6A4F' }} />
            <span className="font-body" style={{ fontSize: 11, color: '#2D6A4F', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
              Public validation dashboard
            </span>
          </div>
          <h1 className="font-display" style={{ fontSize: 38, fontWeight: 400, color: '#1B4332', letterSpacing: '-0.01em', lineHeight: 1.1, marginBottom: 8 }}>
            How accurate are we, really?
          </h1>
          <p className="font-body" style={{ fontSize: 14.5, lineHeight: 1.65, color: '#4F4B44', maxWidth: 760 }}>
            Every Phase 1 module must pass a validation pilot against real ICAR or State Agricultural University data
            before reaching production. This page publishes the actual on-the-ground accuracy of each module — including
            the misses. We'd rather show a gap honestly than market a number we can't defend.
          </p>

          <button
            onClick={() => navigate('/predict/validation-log')}
            className="inline-flex items-center gap-2 mt-5 transition-all"
            style={{
              fontSize: 13, fontWeight: 600, color: '#2D6A4F',
              background: '#EAF2E5', border: '1px solid #C8DBC0',
              padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#D8E5DC'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#EAF2E5'; }}
            data-testid="open-validation-log"
          >
            <History size={14} />
            View full validation log
            <ArrowRight size={14} />
          </button>

          {user && (
            <button
              onClick={() => navigate('/predict/admin/submit-pilot')}
              className="inline-flex items-center gap-2 mt-5 ml-3 transition-all"
              style={{
                fontSize: 13, fontWeight: 600, color: '#FFFFFF',
                background: '#2D6A4F', border: '1px solid #1B4332',
                padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
              }}
              data-testid="open-admin-submit-pilot"
            >
              <Send size={13} />
              Submit pilot result
              <ArrowRight size={13} />
            </button>
          )}
        </header>

        <DisclaimerBanner module="validation-dashboard" />

        {loading && (
          <div className="text-center py-16">
            <Loader2 size={26} className="animate-spin mx-auto" style={{ color: '#2D6A4F' }} />
            <p className="font-body mt-2" style={{ fontSize: 13, color: '#6F6A63' }}>Loading validation summary…</p>
          </div>
        )}

        {error && !loading && (
          <div
            className="rounded-xl p-5"
            style={{ background: '#FFFBF1', border: '1px solid #F5E5B8', color: '#6B5500' }}
            data-testid="validation-error"
          >
            <p className="font-body" style={{ fontSize: 13.5 }}>
              <strong>Could not load validation summary:</strong> {error}
            </p>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Module list */}
            <section data-testid="validation-modules">
              <div className="grid grid-cols-1 gap-4">
                {data.per_module.map((m) => (
                  <ModuleRow key={m.module_key} m={m} target={TARGETS[m.module_key]} />
                ))}
              </div>
            </section>

            {/* Empty state callout */}
            {data.total_pilots === 0 && (
              <section
                className="mt-10 rounded-2xl p-7 text-center"
                style={{ background: '#FAFBF9', border: '1px dashed #BCC5B5' }}
                data-testid="validation-empty-state"
              >
                <Inbox size={32} style={{ color: '#8A8580' }} className="mx-auto mb-3" />
                <h3 className="font-display" style={{ fontSize: 22, color: '#1B4332', marginBottom: 6, fontWeight: 400 }}>
                  No pilot data submitted yet
                </h3>
                <p className="font-body mx-auto" style={{ fontSize: 13.5, color: '#6F6A63', lineHeight: 1.65, maxWidth: 580 }}>
                  {data.note}
                </p>
                <p className="font-body mt-4" style={{ fontSize: 12.5, color: '#8A8580' }}>
                  Generated at {new Date(data.generated_at).toLocaleString()}.
                </p>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function ModuleRow({ m, target }) {
  const ms = m.metrics_summary;
  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: '#FFFFFF', border: '1px solid #E5E7DD' }}
      data-testid={`validation-row-${m.module_key}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display" style={{ fontSize: 19, color: '#1B4332', fontWeight: 500, marginBottom: 4 }}>
            {m.label}
          </h3>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            <ValidationStatus status={m.status} lastValidated={m.last_validated} />
            <span className="font-body" style={{ fontSize: 12, color: '#6F6A63' }}>
              {m.n_pilots} pilot{m.n_pilots === 1 ? '' : 's'} on file
              {ms?.total_observations ? ` · ${ms.total_observations} observations` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Real metrics surface — only when pilots have been submitted */}
      {ms && m.n_pilots > 0 && (
        <div
          className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5"
          data-testid={`validation-metrics-${m.module_key}`}
        >
          <MetricCell label="RMSE (best)" stats={ms.rmse} unit="" testid={`metric-rmse-${m.module_key}`} />
          <MetricCell label="MAE (best)" stats={ms.mae} unit="" testid={`metric-mae-${m.module_key}`} />
          <MetricCell label="Match %" stats={ms.match_pct} unit="%" higherIsBetter testid={`metric-match-${m.module_key}`} />
          <MetricCell label="|Lead/lag| days" stats={ms.lead_lag_days_abs} unit="d" testid={`metric-leadlag-${m.module_key}`} />
        </div>
      )}

      {target && (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg p-3"
          style={{ background: '#F5F2EC', border: '1px solid #E5E7DD' }}
        >
          <Target size={14} style={{ color: '#2D6A4F', marginTop: 3, flexShrink: 0 }} />
          <div>
            <div className="font-body" style={{ fontSize: 10.5, color: '#2D6A4F', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, marginBottom: 2 }}>
              Validation target
            </div>
            <div className="font-body" style={{ fontSize: 13, color: '#3F4A3D', lineHeight: 1.55 }}>
              {target}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCell({ label, stats, unit, higherIsBetter = false, testid }) {
  const empty = !stats || stats.n === 0;
  const best = empty ? null : (higherIsBetter ? stats.worst : stats.best);
  return (
    <div
      className="rounded-lg px-3 py-2.5"
      style={{
        background: empty ? '#FAFBF9' : '#EAF2E5',
        border: `1px solid ${empty ? '#E5E7DD' : '#C8DBC0'}`,
      }}
      data-testid={testid}
    >
      <div
        className="font-body"
        style={{ fontSize: 10, color: empty ? '#A09B95' : '#2D6A4F', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 2 }}
      >
        {label}
      </div>
      <div
        className="font-display"
        style={{ fontSize: 17, color: empty ? '#A09B95' : '#1B4332', fontWeight: 500, lineHeight: 1.1 }}
      >
        {empty ? '—' : `${best}${unit}`}
      </div>
      {!empty && (
        <div className="font-body" style={{ fontSize: 10.5, color: '#6F6A63', marginTop: 2 }}>
          median {stats.median}{unit} · n={stats.n}
        </div>
      )}
    </div>
  );
}
